import React, { useMemo, useState } from 'react';
import { Button, Checkbox, Drawer, Input } from 'antd';
import { BondFamily, CatalogueRow, FilterOption } from './types';
import { instrumentColumns } from './instrumentColumns';
import CatalogueTable from './CatalogueTable';

export default function BondFamilyDrawer({
  family,
  options,
  members,
  matchingIds,
  selected,
  showAssetClass = true,
  disabled,
  onClose,
  onDetails,
  onAdd,
  onRemove,
  onPrepare,
  selectedExtra,
  selectedAction,
}: {
  family: BondFamily;
  options: FilterOption[];
  members: CatalogueRow[];
  matchingIds: Set<number>;
  selected: Set<number>;
  showAssetClass?: boolean;
  disabled?: boolean;
  onClose: () => void;
  onDetails: (row: CatalogueRow) => void;
  onAdd?: (ids: number[]) => void;
  onRemove?: (ids: number[]) => void;
  onPrepare?: () => void;
  selectedExtra?: (id: number) => React.ReactNode;
  selectedAction?: (id: number) => React.ReactNode;
}) {
  const [query, setQuery] = useState('');
  const [onlySelected, setOnlySelected] = useState(false);
  const rows = useMemo(
    () =>
      members
        .filter(
          row =>
            (!onlySelected || selected.has(row.id)) &&
            row.searchText.includes(query.trim().toLowerCase()),
        )
        .sort(
          (a, b) =>
            Number(matchingIds.has(b.id)) - Number(matchingIds.has(a.id)) ||
            (b.issue_date || '').localeCompare(a.issue_date || '') ||
            b.id - a.id,
        ),
    [members, query, onlySelected, selected, matchingIds],
  );
  const selectedCount = members.filter(row => selected.has(row.id)).length;
  const showMatches = members.some(row => !matchingIds.has(row.id));
  const showControls = !!(selectedExtra || selectedAction);
  return (
    <Drawer
      open
      classNames={{ body: 'ac-family-body' }}
      size="large"
      title={`${family.code} · Synthetic bond family`}
      onClose={onClose}
    >
      <p className="ac-muted">
        {members.length} issues ·{' '}
        {members.filter(row => matchingIds.has(row.id)).length} match catalogue
        filters
        {selectedCount > 0 && ` · ${selectedCount} selected`}
      </p>
      <div className="ac-issue-toolbar">
        <Input.Search
          aria-label="Search family issues"
          placeholder="Ticker, ISIN or name"
          value={query}
          onChange={event => setQuery(event.target.value)}
          allowClear
        />
        {onPrepare && (
          <Button disabled={disabled} onClick={onPrepare}>
            Prepare for session
          </Button>
        )}
        {onAdd && (
          <Checkbox
            checked={onlySelected}
            onChange={event => setOnlySelected(event.target.checked)}
          >
            Selected issues only
          </Checkbox>
        )}
      </div>
      {onAdd && (
        <p className="ac-muted">
          Choose individual issues below, or prepare the family using the
          session’s saved dates and switchover settings.
        </p>
      )}
      <CatalogueTable<CatalogueRow>
        className="ac-instrument-table"
        size="small"
        tableLayout="auto"
        scroll={{ x: '100%' }}
        rowKey="id"
        dataSource={rows}
        pagination={{ defaultPageSize: 15, hideOnSinglePage: true }}
        rowSelection={
          onAdd && onRemove
            ? {
                selectedRowKeys: [...selected],
                getCheckboxProps: row => ({
                  disabled,
                  'aria-label': `Select issue ${row.ticker}`,
                }),
                onSelect: (row, checked) => {
                  if (!disabled) (checked ? onAdd : onRemove)([row.id]);
                },
                onSelectAll: (checked, _, changed) => {
                  if (!disabled)
                    (checked ? onAdd : onRemove)(changed.map(row => row.id));
                },
              }
            : undefined
        }
        columns={instrumentColumns<CatalogueRow>({
          options,
          showAssetClass,
          members: row => [row],
          onOpen: onDetails,
          extraColumns: [
            {
              title: 'Issue date',
              dataIndex: 'issue_date',
            },
            {
              title: 'Maturity',
              dataIndex: 'maturity_date',
            },
            {
              title: 'Availability',
              key: 'availability',
              render: (_, row) =>
                row.retired ? 'Retired' : row.visible ? 'Visible' : 'Hidden',
            },
            ...(showMatches
              ? [
                  {
                    title: 'Matches filters',
                    key: 'matches',
                    render: (_: unknown, row: CatalogueRow) =>
                      matchingIds.has(row.id) ? 'Yes' : 'No',
                  },
                ]
              : []),
            ...(showControls
              ? [
                  {
                    title: 'Actions',
                    key: 'actions',
                    render: (_: unknown, row: CatalogueRow) =>
                      selected.has(row.id) && (
                        <div className="ac-issue-controls">
                          {selectedExtra?.(row.id)}
                          {selectedAction?.(row.id)}
                        </div>
                      ),
                  },
                ]
              : []),
          ],
        })}
      />
    </Drawer>
  );
}
